import { zodResolver } from '@hookform/resolvers/zod';
import type { Control } from 'react-hook-form';
import { useForm } from 'react-hook-form';

import { Select } from '@/components/common/select';
import { Drawer } from '@/components/feedback/drawer';
import { FormField } from '@/components/forms';
import { onlyEnabled } from '@/config/preview-features';

import { customerDeviceToDefaults, customerDeviceToFilters } from '../device-filters';
import {
  CONNECTOR_STATUS_CHIP,
  DEVICE_STATUS_CHIP,
  DEVICE_TYPE_OPTIONS,
  SUBSCRIPTION_STATUS_CHIP,
} from '../format';
import { customerDeviceFiltersSchema } from '../schemas';
import type { CustomerDeviceFilterFormValues, CustomerDeviceFilters } from '../types';

const DEVICE_STATUS_OPTIONS = (
  Object.keys(DEVICE_STATUS_CHIP) as (keyof typeof DEVICE_STATUS_CHIP)[]
).map((value) => ({ value, label: DEVICE_STATUS_CHIP[value].label }));

const CONNECTOR_STATUS_OPTIONS = (
  Object.keys(CONNECTOR_STATUS_CHIP) as (keyof typeof CONNECTOR_STATUS_CHIP)[]
).map((value) => ({ value, label: CONNECTOR_STATUS_CHIP[value].label }));

const SUBSCRIPTION_STATUS_OPTIONS = (
  Object.keys(SUBSCRIPTION_STATUS_CHIP) as (keyof typeof SUBSCRIPTION_STATUS_CHIP)[]
).map((value) => ({ value, label: SUBSCRIPTION_STATUS_CHIP[value].label }));

type Option = { value: string; label: string };

type FilterSelectProps = {
  name: keyof CustomerDeviceFilterFormValues;
  control: Control<CustomerDeviceFilterFormValues>;
  label: string;
  options: Option[];
};

/**
 * A single-select filter field that clears to `null` rather than `undefined`.
 *
 * antd's clear button fires `onChange(undefined)`, and RHF treats an `undefined` value as
 * "reset to the field's `defaultValue`" — so on a re-opened drawer (seeded from the last applied
 * filter) clearing would snap the select back to its seeded value instead of emptying it.
 * Normalising the cleared value to `null` (a *defined* value RHF keeps) clears it; the schema
 * accepts `null` (`.nullish()`) and `toFilters` folds it back to "no filter". We wrap `FormField` +
 * `Select` directly because the shared `<SelectField>` hardcodes `onChange={field.onChange}`.
 */
function FilterSelect({ name, control, label, options }: FilterSelectProps) {
  return (
    <FormField name={name} control={control} label={label}>
      {({ field, fieldState }) => (
        <Select
          id={name}
          style={{ width: '100%' }}
          placeholder="Select"
          allowClear
          options={options}
          status={fieldState.error ? 'error' : undefined}
          value={field.value ?? undefined}
          onChange={(next) => field.onChange(next ?? null)}
          onBlur={field.onBlur}
          ref={field.ref}
        />
      )}
    </FormField>
  );
}

export type CustomerDeviceFilterDrawerProps = {
  open: boolean;
  /** Last applied filters; the draft re-seeds from this on each open. */
  value: CustomerDeviceFilters;
  /** Distinct category NAMES from the currently-loaded page (no master Category endpoint). */
  categoryOptions: Option[];
  onApply: (filters: CustomerDeviceFilters) => void;
  onClose: () => void;
};

type BodyProps = Omit<CustomerDeviceFilterDrawerProps, 'open'>;

function FilterBody({ value, categoryOptions, onApply, onClose }: BodyProps) {
  // Re-seed from the applied filters on each open (this body remounts on open). `toDefaults` yields
  // the applied value or `undefined`; the schema is `.nullish()` so both are valid.
  const form = useForm<CustomerDeviceFilterFormValues>({
    resolver: zodResolver(customerDeviceFiltersSchema),
    defaultValues: customerDeviceToDefaults(value),
    mode: 'onChange',
  });

  const handleApply = form.handleSubmit((values) => {
    onApply(customerDeviceToFilters(values));
    onClose();
  });

  return (
    <Drawer
      open
      onClose={onClose}
      title="Filter"
      width={400}
      primaryText="Apply"
      onPrimary={handleApply}
      cancelText="Cancel"
      onCancel={onClose}
    >
      {/* Order is AC 102104's own: Device Category, Device Type, Device Status, Subscription
          Status. Device Type was absent entirely and Subscription Status sat above Device Status
          (ADO 119225). Story 113560 adds Connector Status "alongside" those four; it sits
          directly before Subscription Status, mirroring the column's own position. */}
      <FilterSelect
        name="category"
        control={form.control}
        label="Category"
        options={categoryOptions}
      />
      <FilterSelect
        name="deviceType"
        control={form.control}
        label="Device Type"
        options={onlyEnabled(DEVICE_TYPE_OPTIONS)}
      />
      <FilterSelect
        name="deviceStatus"
        control={form.control}
        label="Device Status"
        options={DEVICE_STATUS_OPTIONS}
      />
      <FilterSelect
        name="connectorStatus"
        control={form.control}
        label="Connector Status"
        options={CONNECTOR_STATUS_OPTIONS}
      />
      <FilterSelect
        name="subscriptionStatus"
        control={form.control}
        label="Subscription Status"
        options={SUBSCRIPTION_STATUS_OPTIONS}
      />
    </Drawer>
  );
}

export function CustomerDeviceFilterDrawer({ open, ...rest }: CustomerDeviceFilterDrawerProps) {
  if (!open) {
    return (
      <Drawer open={false} onClose={rest.onClose}>
        {null}
      </Drawer>
    );
  }
  // Unmount on close; remounts fresh on open so the draft re-seeds from the last applied `value`.
  return <FilterBody {...rest} />;
}
